/**
 * The answers to an `ask_user` question, shared by every way of answering it: the desktop form, the phone's
 * quick-reply chips and the phone question sheet. One question is a single unlabelled field; several
 * (`questions`) are labelled fields that may be multi-select, optional, or typed (no options). The answer goes
 * back as one message: the pick for a single question, a line per question for several.
 */
import { computed, reactive, watch } from 'vue'
import type { UiAskField, UiQuestion } from './reducer'

export const SKIP_TEXT = 'Skip this question and use your best judgment.'

export function useAskForm(question: () => UiQuestion) {
    /** Several labelled questions, or the single question's options as one unlabelled field. */
    const labelled = computed(() => question().fields.length > 0)
    const fields = computed<UiAskField[]>(() => (labelled.value
        ? question().fields
        : [{ question: '', options: question().options, multi: false, optional: false }]))

    /** Per field: the picked option indexes (`options.length` is the "Something else" row) and the typed text. */
    const picks = reactive<number[][]>([])
    const custom = reactive<string[]>([])
    const skipped = reactive<boolean[]>([])
    watch(() => question().id, () => {
        picks.splice(0, picks.length, ...fields.value.map(() => []))
        custom.splice(0, custom.length, ...fields.value.map(() => ''))
        skipped.splice(0, skipped.length, ...fields.value.map(() => false))
    }, { immediate: true })

    const otherIndex = (fi: number) => fields.value[fi].options.length
    const isOn = (fi: number, i: number) => picks[fi]?.includes(i) ?? false

    function valuesFor(fi: number): string[] {
        const f = fields.value[fi]
        const text = (custom[fi] ?? '').trim()
        if (!f.options.length) return text ? [text] : []
        const out = (picks[fi] ?? []).filter((i) => i < f.options.length).sort((a, b) => a - b).map((i) => f.options[i])
        if (isOn(fi, otherIndex(fi)) && text) out.push(text)
        return out
    }
    const answeredCount = computed(() => fields.value.filter((_, fi) => valuesFor(fi).length > 0).length)
    const answer = computed(() => {
        if (!labelled.value) return valuesFor(0)[0] ?? ''
        if (!answeredCount.value) return ''
        return fields.value.map((f, fi) => {
            const v = valuesFor(fi)
            const label = /[?:]$/.test(f.question) ? f.question : `${f.question}:`
            return `${label} ${v.length ? v.join(', ') : skipped[fi] ? '(skipped)' : '(left blank)'}`
        }).join('\n')
    })
    /** What to send: the answer, or "use your best judgment" when nothing was answered at all. */
    const message = computed(() => (answeredCount.value ? answer.value : SKIP_TEXT))

    /** Pick option `i` of field `fi` (the "Something else" row is `otherIndex(fi)`). */
    function pick(fi: number, i: number) {
        const f = fields.value[fi]
        const other = i === otherIndex(fi)
        if (!f.multi) picks[fi] = [i]
        else if (other) { if (!isOn(fi, i)) picks[fi] = [...picks[fi], i] }
        else picks[fi] = isOn(fi, i) ? picks[fi].filter((x) => x !== i) : [...picks[fi], i]
        skipped[fi] = false
    }
    /** Typing in "Something else" selects it (and clearing it deselects it on multi-select questions). */
    function onCustomInput(fi: number) {
        const o = otherIndex(fi)
        skipped[fi] = false
        if (!fields.value[fi].options.length) return
        if (!fields.value[fi].multi) picks[fi] = [o]
        else if (custom[fi].trim() && !isOn(fi, o)) picks[fi] = [...picks[fi], o]
        else if (!custom[fi].trim()) picks[fi] = picks[fi].filter((x) => x !== o)
    }
    /** The typed text of field `fi` changed (the "Something else" line, or a typed answer). */
    function setCustom(fi: number, text: string) {
        custom[fi] = text
        onCustomInput(fi)
    }
    function skipField(fi: number) {
        picks[fi] = []
        custom[fi] = ''
        skipped[fi] = true
    }
    /** The question can go on: answered, or optional. */
    const ready = (fi: number) => fields.value[fi].optional || valuesFor(fi).length > 0

    return { labelled, fields, picks, custom, skipped, otherIndex, isOn, valuesFor, answeredCount, answer, message, pick, onCustomInput, setCustom, skipField, ready }
}

export type AskForm = ReturnType<typeof useAskForm>
